import React, { useContext, useEffect, useState } from 'react';
import debounce from 'debounce';
import md5 from 'md5';

import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'

import FireDataContext from '../../../services/firebase/FireDataContext';
import OrganizationMemberInput from './OrganizationMemberInput';
import AuthContext from '../../../services/firebase/AuthContext';
import Grid from '@material-ui/core/Grid'
import Spacer from '../../../app/common/ui/Spacer';
import { timestamp } from '../../../app/data/validate';

const validateEmail = email => {
    // eslint-disable-next-line
    const re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email.trim());
}

const useStyles = makeStyles((theme) => ({
    paper: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1),
    },
    field: {
        margin: theme.spacing(1),
    },
    button: {
        margin: theme.spacing(1),
    }
}));

const OrganizationMembers = ({ id }) => {
    const { currentUser } = useContext(AuthContext);
    const db = useContext(FireDataContext);
    const [members, setMembers] = useState([]);
    const [status, setStatus] = useState(undefined);

    useEffect(() => {
        (async () => {
            const data = await db.all({ path: `organizations/${id}/members` })
            setMembers(data);
        })();

    }, [db, id]);

    const addNewMember = () => {
        const member = { name: '', email: '', role: 'Solicitor' };
        const path = `organizations/${id}/members`;

        db.insert({
            path,
            payload: member
        })
            .then(result => {
                // console.log('addNewMember', member.id, member.name);
                setMembers([...members, { ...member, id: result.id }]);
            })
            .catch(error => console.log('OrganizationMembers > addNewMember > error', error));
    }

    const handleChange = debounce((changeItemIndex, fragment) => {
        const member = { ...members[changeItemIndex], ...fragment };

        setMembers(
            members.map((item, index) => {
                return index === changeItemIndex ? member : item;
            })
        );

        (async () => {
            try {
                const users = await db.get({
                    path: 'users',
                    query: { field: 'email', operator: '==', value: member.email },
                    context: 'OrganizationMembers'
                });
                
                if (users?.length) {
                    await db.update({
                        path: users[0]._docPath,
                        payload: {
                            phoneNumber: member.phoneNumber || '',
                            name: member.name || users[0].name || '',
                            role: member.role || '',
                        },
                        context: 'OrganizationMembers'
                    })
                }
                else {
                    if (member.pseudo) {
                        // pseudo members are created for referrers who don't want to sign up to TICIT themselves
                        await db.update({
                            path: `users/${member.id}`,
                            payload: {
                                ...member,
                                organization: id,
                                uid: currentUser.uid,
                                ts: timestamp(),
                                roles: ['customer'],
                                verified: false
                            },
                            context: 'OrganizationMembers'
                        })
                    }
                }
            }
            catch (error) {
                console.log(error);
            }
        })();

        db.update({
            path: `organizations/${id}/members/${member.id}`,
            payload: member,
            context: 'OrganizationMembers'
        })
            .then(() => {
                if (validateEmail(member.email)) {
                    if (!member.pseudo) {
                        db.update({
                            path: `invitations/${md5(member.email.trim().toLowerCase())}`,
                            payload: {
                                roles: ['customer'],
                                invitedBy: { uid: currentUser.uid, email: currentUser.email },
                                invitedAt: new Date().valueOf(),
                                organization: id,
                                phoneNumber: member.phoneNumber || ''
                            },
                            context: 'OrganizationMembers'
                        })
                            .catch(error => console.log(error));
                    }
                }



            })
            .catch(error => console.log('OrganizationMembers > update > error', error));

    }, 500);

    const deleteMember = index => {
        if (members[index].id) {
            db.remove({ path: `organizations/${id}/members/${members[index].id}` })
                .then(() => {
                    setStatus({ severity: 'success', message: `${members[index].name} has been removed` });
                })
                .catch(error => console.log('OrganizationMembers > delete > error', error));
        }
        const remainder = members.filter((_, i) => i !== index);
        setMembers(remainder);
    }

    const classes = useStyles();

    return (
        <div>
            <Spacer />
            <Typography variant="h5" color="textSecondary">Referrers</Typography>
            <Grid container spacing={1} alignContent="center">
                <Grid item>
                    <Button variant="contained" color="secondary" onClick={addNewMember} className={classes.button}>
                        Add a new referrer
                    </Button>
                </Grid>
            </Grid>
            <div>
                {
                    status && <Alert className={classes.button} severity={status.severity} variant="filled" onClose={() => setStatus(undefined)}>{status.message}</Alert>
                }
            </div>
            <Spacer />
            <Grid container spacing={2} >
                {
                    members?.map((m, i) => {
                        return <OrganizationMemberInput key={m.id || i} index={i} item={m} onChange={handleChange} onDelete={deleteMember} classes={classes} />
                    })
                }
            </Grid>
        </div>
    );


};

export default OrganizationMembers;